import React from 'react';
import './view-toggle.css';

/**
 * Two-option animated toggle rendered as a single button. A purple thumb
 * carrying the inactive option's icon slides between the left and right edge;
 * the active option's icon + label crossfades into the space opposite the thumb.
 * Both label groups stay mounted (they fade rather than mount/unmount) so their
 * transitions can run. Clicking anywhere on the control switches to the other
 * option's key; clicking while disabled does nothing.
 *
 * @param {Object} props
 * @param {Array<{key: string, label: string, icon: React.ReactNode}>} props.options
 *   Exactly two options, rendered in array order (index 0 = left, index 1 = right).
 * @param {string} props.value - The key of the currently active option.
 * @param {(key: string) => void} props.onChange - Called with the inactive option's
 *   key when the user activates the control.
 * @param {boolean} [props.disabled=false] - When true, dims the control and blocks interaction.
 * @param {string} [props.ariaLabel] - Optional tooltip; surfaced as the button `title`.
 *   The accessible name is always "Switch to {inactive label}".
 * @returns {JSX.Element}
 */
export default function ViewToggle({
  options, value, onChange, disabled = false, ariaLabel,
}) {
  const inactiveIndex = options.findIndex((option) => option.key !== value);
  const inactiveOption = options[inactiveIndex];

  // The thumb sits over the inactive option's home side (index 0 = left edge,
  // index 1 = right edge). Container is 173px wide, thumb 41px, so the right
  // edge is 173 - 41 = 132px in.
  const thumbOffset = inactiveIndex === 0 ? 0 : 132;

  const handleClick = () => {
    if (!disabled) onChange(inactiveOption.key);
  };

  return (
    <button
      type="button"
      data-testid="view-toggle"
      data-active={value}
      aria-label={`Switch to ${inactiveOption.label}`}
      title={ariaLabel}
      disabled={disabled}
      onClick={handleClick}
      className={`pp-view-toggle${disabled ? ' pp-view-toggle--disabled' : ''}`}
    >
      <span
        className="pp-view-toggle__thumb"
        aria-hidden="true"
        style={{ transform: `translateX(${thumbOffset}px)` }}
      >
        <span className="pp-view-toggle__icon">{inactiveOption.icon}</span>
      </span>

      {options.map((option, index) => {
        const active = option.key === value;
        return (
          <span
            key={option.key}
            data-testid={`view-toggle-label-${option.key}`}
            data-active={active}
            className={`pp-view-toggle__label-group${index === 0 ? ' pp-view-toggle__label-group--left' : ' pp-view-toggle__label-group--right'}${active ? ' is-active' : ''}`}
          >
            <span className="pp-view-toggle__icon" aria-hidden="true">{option.icon}</span>
            <span className="pp-view-toggle__label">{option.label}</span>
          </span>
        );
      })}
    </button>
  );
}
